import type {ReactElement} from 'react';
import {useRef} from 'react';
import React, {useEffect, useState} from 'react';
import styles from './email-campaign-optimal-sending-panel.module.scss';
import type {MaxOptimalTimePayload, OptimalTimeResponse} from '@Components/email-marketing-campaign-performance/email-marketing-campaign-performance.types';
import {Checkbox} from '@Components/checkbox';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Icon} from '@Components/icon-v2';
import {Text, TextSize} from '@Components/text';
import {Thumbnail} from '@Components/thumbnail';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import aiGradientSpinner from '@Assets/ai-gradient-spinner.svg';
import {
  getReadableDateForOptimalSending,
  isCurrentTimeEqualToOrGreaterThanOptimalTimeComputed,
  isOptimalSendTimeforScheduledEmailsValid,
} from '@Components/email-marketing-campaigns/components/email-campaign-scheduler-best-time-control/email-campaign-scheduler-best-time-library';
import {mailingListUpdateEvent} from '@/email-maker/grapesjs-email-editor-plugin/src/email-maker.types';
import {noop} from 'lodash';
import {computeClientTimezoneOffset} from '@Utils/date.util';
import {ClickableDiv} from '@Components/clickable-div';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import {useContainerQuery} from '@Hooks/useContainerQuery';

export interface EmailCampaignOptimalSendingProps {
  emailMarketingCampaignId: string;
  onUpdateSendBestTimeToggle: (updatedValue: boolean) => void;
  prefilledDate?: Date;
  defaultToggleValue?: boolean;
  onUpdateMaxOptimalTime?: (utcTimestamp: number) => void;
}

const containerQuery: ContainerQueryObject = {
  small: {
    minWidth: 0,
    maxWidth: 400,
  },
  large: {
    minWidth: 401,
    maxWidth: 1149,
  },
};

export function EmailCampaignOptimalSendingPanel({
  emailMarketingCampaignId,
  onUpdateSendBestTimeToggle,
  prefilledDate,
  defaultToggleValue = false,
  onUpdateMaxOptimalTime = noop,
}: EmailCampaignOptimalSendingProps): ReactElement {
  const [shouldSendAtBestTime, setShouldSendatBestTime] = useState(false);
  const [maxOptimalTime, setMaxOptimalTime] = useState<null | number>(null);
  const [scheduledOptimalSendingError, setScheduledOptimalSendingError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const params = useContainerQuery(containerRef, containerQuery);

  useEffect(() => {
    setShouldSendatBestTime(defaultToggleValue);
  }, [defaultToggleValue]);

  const computeNewOptimalTimeForUnscheduledEmails = (): void => {
    const maxOptimalTimePayload: MaxOptimalTimePayload = {
      idEmailMarketingCampaign: emailMarketingCampaignId,
      clientTimezoneOffset: computeClientTimezoneOffset(),
    };

    window.PMW.writeLocal('emailmarketing/getOptimalSendingTimeForNonScheduledEmails', maxOptimalTimePayload)
      .then((response: OptimalTimeResponse) => {
        setMaxOptimalTime(response.maximumUtcTimestamp);
        onUpdateMaxOptimalTime(response.maximumUtcTimestamp);
      })
      .catch((error: unknown): void => {
        console.log('Error fetching optimal time: ', error);
      });
  };

  const computeNewOptimalTimeForScheduledEmails = (): void => {
    if (!prefilledDate) {
      return;
    }

    const midnightDate = new Date(prefilledDate);
    midnightDate.setHours(0, 0, 0, 0);

    const maxOptimalTimePayload: MaxOptimalTimePayload = {
      idEmailMarketingCampaign: emailMarketingCampaignId,
      clientTimezoneOffset: computeClientTimezoneOffset(),
      scheduledDate: midnightDate.toString(),
    };

    window.PMW.writeLocal('emailmarketing/getOptimalSendingTimeForScheduledEmails', maxOptimalTimePayload)
      .then((response: OptimalTimeResponse) => {
        if (!isOptimalSendTimeforScheduledEmailsValid(response)) {
          setScheduledOptimalSendingError(true);
          setMaxOptimalTime(null);
          onUpdateSendBestTimeToggle(false);
          setShouldSendatBestTime(false);
          return;
        }

        setMaxOptimalTime(response.maximumUtcTimestamp);
        setScheduledOptimalSendingError(false);
        onUpdateMaxOptimalTime(response.maximumUtcTimestamp);
      })
      .catch((error: unknown): void => {
        console.log(error);
      });
  };

  const onMailingListUpdate = (): void => {
    if (!prefilledDate) {
      computeNewOptimalTimeForUnscheduledEmails();
      return;
    }

    computeNewOptimalTimeForScheduledEmails();
  };

  useEffect(() => {
    window.addEventListener(mailingListUpdateEvent, onMailingListUpdate);

    return (): void => {
      window.removeEventListener(mailingListUpdateEvent, onMailingListUpdate);
    };
  }, []);

  useEffect(() => {
    if (maxOptimalTime === null) {
      if (!prefilledDate) {
        computeNewOptimalTimeForUnscheduledEmails();
        return;
      }

      if (!scheduledOptimalSendingError) {
        computeNewOptimalTimeForScheduledEmails();
      }
    }

    const interval = setInterval(() => {
      if (isCurrentTimeEqualToOrGreaterThanOptimalTimeComputed(maxOptimalTime)) {
        setMaxOptimalTime(null);
      }
    }, 1000);

    return (): void => {
      clearInterval(interval);
    };
  }, [maxOptimalTime]);

  const getCheckboxClasses = (): string => {
    if (shouldSendAtBestTime) {
      return styles.checked;
    }

    return `${styles.unchecked} ${scheduledOptimalSendingError ? styles.error : null}`;
  };

  const onClickCheck = (): void => {
    if (!maxOptimalTime) {
      return;
    }

    const newState = !shouldSendAtBestTime;
    onUpdateSendBestTimeToggle(newState);
    setShouldSendatBestTime(newState);
  };

  const getCheckbox = (): ReactElement => {
    return <Checkbox onClick={onClickCheck} className={`${styles.checkbox} ${getCheckboxClasses()}`} size={IconSize.SIZE_ICON_20} isChecked={shouldSendAtBestTime} />;
  };

  const getIconEffects = (): ReactElement => {
    return <Icon className={styles.iconClasses} icon="icon-ai" size={IconSize.SIZE_ICON_20} />;
  };

  const getToggleDescription = (): ReactElement => {
    const text = params.small ? window.i18next.t('pmwjs_best_time') : window.i18next.t('pmwjs_send_best_time');
    return <Text bold className={`${styles.textDescription} body-xs-bold`} val={text} />;
  };

  const shouldShowBestTimeComputationLoader = (): boolean => {
    if (!shouldSendAtBestTime || scheduledOptimalSendingError) {
      return false;
    }

    return maxOptimalTime === null;
  };

  const getBestTimeComputationLoaderIfNeeded = (): ReactElement | null => {
    if (!shouldShowBestTimeComputationLoader()) {
      return null;
    }

    return (
      <div className={styles.gradientLoader}>
        <Thumbnail
          data={{
            type: ThumbnailType.IMAGE,
            imageSrc: aiGradientSpinner as string,
          }}
          className={styles.gradientSVGSpinner}
        />
      </div>
    );
  };

  const getBestTimeLoaderTextIfNeeded = (): ReactElement | null => {
    if (!shouldShowBestTimeComputationLoader()) {
      return null;
    }

    return (
      <div className="spacing-m-l-3">
        <Text className={styles.textDescriptionLoader} size={TextSize.XSMALL} val={window.i18next.t('pmwjs_calculating_best_time')} />{' '}
      </div>
    );
  };

  const getBestTimeIfNeeded = (): ReactElement | null => {
    if (shouldSendAtBestTime && maxOptimalTime) {
      const readableOptimalDateTime = getReadableDateForOptimalSending(maxOptimalTime);

      return (
        <div className={styles.bestTimeDescription}>
          <Text className={`${styles.textDescriptionLoader} ${styles.emailsSentByText}`} size={TextSize.XSMALL} val={window.i18next.t('pmwjs_all_emails_will_be_sent_by')} />
          <Text bold className={styles.textDescriptionLoader} size={TextSize.XSMALL} val={readableOptimalDateTime} />
        </div>
      );
    }

    return null;
  };

  const getClassesForErrorStateIfNeeded = (): string => {
    if (scheduledOptimalSendingError) {
      return styles.error;
    }

    return '';
  };

  const getBestTimeToggle = (): ReactElement => {
    return (
      <div className={styles.bestTimeToggle}>
        <div className={styles.leftPanel}>
          <ClickableDiv className={styles.checkboxClickableDiv} onClick={onClickCheck}>
            {getCheckbox()} {getIconEffects()} {getToggleDescription()}
          </ClickableDiv>
        </div>
        <div className={styles.rightPanel}>
          {getBestTimeComputationLoaderIfNeeded()} {getBestTimeLoaderTextIfNeeded()}
          {getBestTimeIfNeeded()}
        </div>
      </div>
    );
  };

  const getErrorMessagePanel = (): ReactElement | null => {
    if (!scheduledOptimalSendingError || !prefilledDate) {
      return null;
    }

    return (
      <div className={styles.errorMessageTextWrapper}>
        <Text size={TextSize.XXSMALL} val={window.i18next.t('pmwjs_best_time_today')} />
        <Text size={TextSize.XSMALL} bold val={window.i18next.t('pmwjs_passed')} />{' '}
      </div>
    );
  };

  return (
    <div ref={containerRef} className={`${styles.panelWrapper} ${getClassesForErrorStateIfNeeded()}`}>
      {getBestTimeToggle()} {getErrorMessagePanel()}
    </div>
  );
}
